import React, { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";

export default function Contact({ noMargin }) {

  /* ───────────────────── Magnetic Button ───────────────────── */
  const btnRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const magneticX = useTransform(mouseX, [-50, 50], [-15, 15]);
  const magneticY = useTransform(mouseY, [-50, 50], [-15, 15]);

  const handleMouseMove = (e) => {
    const rect = btnRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    mouseX.set(x);
    mouseY.set(y);
  };

  const resetPosition = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Animation variants for on-scroll (whileInView) animations
  const sectionParent = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-16 bg-gradient-to-br from-black  to-[var(--bg)]" style={noMargin ? undefined : { marginTop: "90px" }}>

      {/* ───────────────────── Main Contact Row ───────────────────── */}
      <motion.div
        variants={sectionParent}
        initial="hidden"
        whileInView="show"
        viewport={{ once: false, amount: 0.2 }}
        className="w-full max-w-5xl flex flex-col md:flex-row gap-10"
      >
        {/* ── Left Info Section ─────────────────────────────── */}
        <motion.div
          variants={fadeUp}
          className="flex-1"
        >
          <h1 className="text-4xl md:text-5xl font-semibold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-gray-300 mb-8 leading-relaxed">
            Have a project in mind? 
            We’re here to help — Connect with Skyline MEP Consultants.
          </p>

          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="p-6 bg-black/20 backdrop-blur-md border border-white/20 rounded-xl text-gray-300 space-y-2"
          >
            <p className="text-lg font-semibold text-white">📍 Flat No.1A, 2nd Floor, Datta Krupa Co-Op Housing Society, Jai Laxmi Building, Sr. No. 98, Plot No. 
21, Right Bhusari Colony, Paud Road, Kothrud, Pune - 411038</p>
            <p>📧 skylinemepconsultants@gmail.com</p>
            <p>📞 +91 9890621049</p>
          </motion.div>
        </motion.div>

        {/* ── Form Section ─────────────────────────────── */}
        <motion.form
          variants={fadeUp}
          className="flex-1 p-8 bg-black/20 backdrop-blur-xl border border-white/20 rounded-2xl shadow-lg"
        >
          <motion.div
            variants={sectionParent}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.2 }}
            className="space-y-6"
          >
            {[
              "Name", "Email", "Subject"
            ].map((label, i) => (
              <motion.div key={i} variants={fadeUp} className="relative">
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 text-white bg-transparent border border-gray-500 rounded-lg
                  focus:outline-none focus:border-white transition-all peer"
                />
                <label className="absolute left-4 top-3 text-gray-400 text-sm transition-all pointer-events-none
                  peer-focus:text-white peer-focus:top-[-10px] peer-focus:text-xs">
                  {label}
                </label>
              </motion.div>
            ))}

            {/* Textarea */}
            <motion.div variants={fadeUp} className="relative">
              <textarea
                required
                rows="4"
                className="w-full px-4 py-3 text-white bg-transparent border border-gray-500 rounded-lg
                focus:border-white focus:outline-none peer"
              ></textarea>
              <label className="absolute left-4 top-3 text-gray-400 text-sm transition-all pointer-events-none
                peer-focus:text-white peer-focus:top-[-10px] peer-focus:text-xs">
                Message
              </label>
            </motion.div>

            {/* Magnetic Submit Button */}
            <motion.div variants={fadeUp}>
              <motion.button
                ref={btnRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={resetPosition}
                style={{ x: magneticX, y: magneticY }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-3 rounded-lg bg-gradient-to-r from-[var(--bg)] to-[var(--bg2)]
                text-white font-semibold tracking-wide shadow-xl hover:shadow-blue-500/40 transition-all relative overflow-hidden"
              >
                Send Message 
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.form>
      </motion.div>

      {/* ───────────────────── Google Map Section ───────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mt-12 w-full max-w-5xl rounded-2xl overflow-hidden shadow-xl border border-white/20"
      >
        <iframe
          title="Google Map"
          width="100%"
          height="350"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          className="rounded-2xl"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.4423589615562!2d73.78860847496254!3d18.50890168258244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf5ea3973bdd%3A0x41f3377eac065326!2sSkyline%20MEP%20consultant!5e0!3m2!1sen!2sin!4v1765114179001!5m2!1sen!2sin"
        ></iframe>
      </motion.div>
    </div>
  );
}
